/**
 * 頁 17 — 未完事項（`ui-spec` §2 頁 17；P1-12；`rules §七 G4`／`H4`）
 *
 * 一覽：**未結算場次**／**缺交收證據**／**在場庫存** —— 「冇一張出庫單失蹤」嘅跟進台
 * （`project-spec` §5 成功定義 5）。
 *
 * 權限：物流／職員／管理員（**供應商 403** —— 後端 `guard()` 擋，前端亦唔顯示入口）。
 * ⚠️ 資料一律由 `GET /api/pending` 出（同儀表板共用 `_lib/pending.ts`）；
 *    「缺交收證據」判斷經 `deliveryEvidence()`（唯一實作）→ 前端唔准自己判斷。
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { errText, hint as hintStyle } from '@coeldery/ui-kit';
import PendingLists from '../components/PendingLists';
import { ApiError, api, type PendingResponse } from '../lib/api';

/** 頁 17 一次撈幾多條（後端上限 200；呢度 100 已經遠超實際營運量） */
const LIMIT = 100;

export default function Pending() {
  const { t } = useTranslation();
  const [data, setData] = useState<PendingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.pending(LIMIT));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const reload = async () => {
    setBusy(true);
    await load();
    setBusy(false);
  };

  if (loading) return <p style={hintStyle}>{t('common.loading')}</p>;

  const counts = data?.counts;

  return (
    <>
      <h2 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: 800 }}>{t('pending.title')}</h2>
      <p style={hintStyle}>{t('pending.intro')}</p>

      {error && (
        <p style={errText} role="alert">
          {error}
        </p>
      )}

      {counts && (
        <p style={{ ...hintStyle, fontWeight: 700 }}>
          {t('pending.summary', {
            events: counts.unsettledEvents,
            chop: counts.missingChop,
            cbm: Number(counts.onSiteCbm ?? 0).toFixed(6),
          })}
        </p>
      )}

      <PendingLists
        unsettledEvents={data?.unsettledEvents ?? []}
        missingChop={data?.missingChop ?? []}
        onSite={data?.onSite ?? []}
        onReload={() => void reload()}
        reloading={busy}
      />
    </>
  );
}
